/* @flow */
import {pick, pickBy} from 'lodash';
import {toCanonicalURL, toURL, syncPets} from '@homeaway/search-url-parser';
import type {SearchRequest} from '@homeaway/search-url-parser';

import {canonicalizeSearchRequest} from './searchParamsUtils';
import {compose} from 'redux';
import {BOUNDING_BOX_PARAMS} from '../constants/map';
import {DEFAULT_PARAMS_TO_CLEAR} from '../constants/router';
import {localizeUrl} from '@homeaway/localization-utils';

type Props = {
    pathname: string,
    search?: string
};

// type SERPURLOptions = {
//     hostname?: string
// };

export const getUri = ({pathname, search}: Props) => {
    return `${pathname}${search || ''}`;
};

export const requestsHaveEquivalentLocaleParam = (
    a: SearchRequest,
    b: SearchRequest
): boolean => {
    const {locale: aLocale} = a;
    const {locale: bLocale} = b;

    return aLocale === bLocale;
};

export const requestsHaveEquivalentPriceFilterParams = (
    a: SearchRequest,
    b: SearchRequest
): boolean => {
    const {minPrice: aMinPrice, maxPrice: aMaxPrice} = a;
    const {minPrice: bMinPrice, maxPrice: bMaxPrice} = b;

    return aMinPrice === bMinPrice && aMaxPrice === bMaxPrice;
};

const getUpdatedSearchParams = (
    newParams?: Object = {},
    paramsToClear?: Array<string> = [],
    state: Object
) => {
    const {
        map: {bounds, zoom},
        expandedGroups: {hasModifiedMapViewport},
        searchParams: {params, inputParams}
    } = state;
    const canonicalParams = canonicalizeSearchRequest(params);

    let mergedParams = {
        ...canonicalParams,
        ...inputParams,
        ...newParams
    };

    const paramsToOmit = [...paramsToClear, ...DEFAULT_PARAMS_TO_CLEAR];

    mergedParams = pickBy(
        mergedParams,
        // Explicit type needed on `key` for Flow
        (value, key: string) => {
            return !paramsToOmit.some((p) => {
                // TODO: Potential polyfill fix, toString() is needed, for IE11 since Symbol(react.portal) are coming in for keys
                return p.toLowerCase() === key.toString().toLowerCase();
            });
        }
    );

    // sync filters with pets included
    mergedParams = compose(canonicalizeSearchRequest, syncPets)(mergedParams);

    if (
        hasModifiedMapViewport &&
        !paramsToOmit.some((omittedParam) =>
            BOUNDING_BOX_PARAMS.includes(omittedParam)
        )
    ) {
        mergedParams = {
            ...mergedParams,
            // use the map bounding box params
            ...bounds,
            zoom,
            // but prefer any incoming bounding box changes
            ...pick(newParams, BOUNDING_BOX_PARAMS)
        };
    }

    return canonicalizeSearchRequest(mergedParams);
};

/**
 * Creates updated URL for the current page given the new params and params to clear.
 *
 * @param newParams
 * @param paramsToClear
 * @param state
 * @param options
 * @returns {string}
 */
export const makeUpdatedSERPUrl = (
    newParams?: Object,
    paramsToClear?: Array<string>,
    state: Object,
    options?: Object = {}
): string => {
    const updatedParams = getUpdatedSearchParams(
        newParams,
        paramsToClear,
        state
    );

    return toURL(updatedParams, options);
};

/**
 * Creates SEO compliant page URLs for the current page given the new page number.
 * It is used for 'next' and 'prev' pagination URLs and rel='prev/next' meta tags.
 *
 * @param page
 * @param state
 * @returns {string}
 */
export const makePageURL = (page: number, state: Object): string => {
    const updatedParams = getUpdatedSearchParams({page}, [], state);
    const siteContext =
        typeof window !== 'undefined' && window.__SITE_CONTEXT__
            ? window.__SITE_CONTEXT__
            : {};

    return localizeUrl(toCanonicalURL(updatedParams), siteContext, {
        absolute: siteContext.hostname ? true : false
    });
};

/**
 * Checks whether URL is absolute
 *
 * @param url
 * @returns {boolean}
 */
export const isAbsoluteURL = (url: string) => /^(?:[a-z]+:)?\/\//i.test(url);

export const makeRelativeUrl = (url: ?string): string =>
    url
        ? decodeURIComponent(url.replace(/^(.*\/\/)?[^\/]+(\/\w\w-\w\w)?/, ''))
        : '';
